/** @file boneLook: how a bone and its joint circle are drawn this frame (RGBA and line width). */
import { Color3 } from '@babylonjs/core/Maths/math.color.js';
import { POSE_LAYOUT as L } from '@zilo/pose3d-pose/layout/poseLayout';
import { BONE_SHAPE as S } from './boneShape';
import type { MarksInput } from './jointMarks';
import { VIEWPORT_COLORS } from './viewportColors';

/** A bone's wire colour (RGBA) and line width in CSS px. */
export type BoneLook = { edge: readonly number[]; width: number };

const WIRE = Color3.FromHexString(VIEWPORT_COLORS.boneWire);
const LIME = Color3.FromHexString('#d8f23a');
const HOVER = Color3.FromHexString('#c8f6ff');
const TWIN = Color3.Lerp(WIRE, Color3.White(), 0.3);

/**
 * Plain: cyan at 90 %, 1.5 px; in the selection's subtree (a selected node or below one): yellow-lime, 2 px; hovered:
 * white-cyan, 2 px; the selection's mirror twin: a brighter cyan, 1.5 px. Shared objects, never mutated.
 */
const LOOKS = {
  plain: { edge: [WIRE.r, WIRE.g, WIRE.b, 0.9], width: S.line },
  selected: { edge: [LIME.r, LIME.g, LIME.b, 1], width: S.boldLine },
  hover: { edge: [HOVER.r, HOVER.g, HOVER.b, 1], width: S.boldLine },
  twin: { edge: [TWIN.r, TWIN.g, TWIN.b, 0.9], width: S.line },
} as const satisfies Record<string, BoneLook>;

/** True when node `i` or one of its ancestors (pose `parentIndex`) is selected. */
function inSelection(m: MarksInput, i: number): boolean {
  let j = i;
  for (let steps = 0; j >= 0 && steps <= m.names.length; steps++) {
    if (m.selected.includes(m.names[j] ?? '')) return true;
    j = m.pose[j * L.stride + L.parentIndex] ?? -1;
  }
  return false;
}

/** The look of node `i`'s bone and joint circle. */
export function boneLook(m: MarksInput, i: number): BoneLook {
  if (m.hover === i) return LOOKS.hover;
  if (inSelection(m, i)) return LOOKS.selected;
  return m.twins.includes(m.names[i] ?? '') ? LOOKS.twin : LOOKS.plain;
}
